import React from "react";
import { Tooltip, Icon } from "antd";
import styled from "styled-components";
import moment from "moment";

import { toBRL } from "~/components/UI/InputCurrency";

/**
 * Resumo do parcelamento em UMA linha, colorida por status — pensado pra
 * escanear o board rápido:
 *   verde   = quitado
 *   âmbar   = ainda a receber (parcelas em aberto, nada vencido)
 *   vermelho= vencido/inadimplente, ou concluído com saldo em aberto
 *
 * O detalhe fino (cada parcela: valor, vencimento, forma de pagamento, status)
 * fica no tooltip — o card mostra só o essencial.
 *
 * ES5-safe (sem optional chaining / nullish coalescing) por causa do build.
 */

var PAYMENT_METHODS = {
  1: "Dinheiro",
  2: "Boleto",
  3: "Cartão",
  4: "Transferência",
  5: "Pix",
  6: "Depósito",
  7: "Cheque",
};

var Line = styled.div`
  font-size: 12px;
  line-height: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 600;
  color: ${function(props) { return props.color || "#8c8c8c"; }};
`;

var TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 14px;
  font-size: 12px;
  padding: 2px 0;
`;

var money = function(value) {
  var num = parseFloat(value);
  if (isNaN(num)) {
    num = 0;
  }
  return "R$ " + toBRL(num);
};

/**
 * Período do painel: um mês ('YYYY-MM') ou um intervalo { from, to } em 'YYYY-MM-DD'.
 */
var isPeriodRange = function(period) {
  return !!period && typeof period === "object" && !!period.from && !!period.to;
};

/**
 * Filtra parcelas pelo vencimento dentro do período (mês 'YYYY-MM' ou { from, to }).
 * Sem período -> retorna todas. Usado para escopar o painel (igual ao Contas a Receber).
 */
export var filterInstallmentsByMonth = function(installments, month) {
  if (!Array.isArray(installments)) {
    return [];
  }
  if (!month) {
    return installments;
  }
  var range = isPeriodRange(month);
  return installments.filter(function(p) {
    if (!p || !p.due_date) {
      return false;
    }
    if (range) {
      var due = String(p.due_date).slice(0, 10);
      return due >= month.from && due <= month.to;
    }
    return String(p.due_date).slice(0, 7) === month;
  });
};

/**
 * Texto do período: "out/2026", "jan/2026 a jun/2026" ou "05/02/2026 a 15/04/2026".
 * long = true usa o mês por extenso ("outubro de 2026").
 */
export var formatPeriodLabel = function(period, long) {
  var monthFormat = long ? "MMMM [de] YYYY" : "MMM/YYYY";
  if (!period) {
    return "todos os meses";
  }
  if (!isPeriodRange(period)) {
    return moment(period, "YYYY-MM").format(monthFormat);
  }
  var from = moment(period.from, "YYYY-MM-DD");
  var to = moment(period.to, "YYYY-MM-DD");
  var wholeMonths = from.date() === 1 && to.isSame(to.clone().endOf("month"), "day");
  if (!wholeMonths) {
    return from.format("DD/MM/YYYY") + " a " + to.format("DD/MM/YYYY");
  }
  if (from.isSame(to, "month")) {
    return from.format(monthFormat);
  }
  return from.format(monthFormat) + " a " + to.format(monthFormat);
};

var isPaid = function(installment) {
  if (!installment) {
    return false;
  }
  if (installment.status === "Pago") {
    return true;
  }
  return installment.paid_at !== null && installment.paid_at !== undefined && installment.paid_at !== "";
};

var isOverdue = function(installment) {
  if (isPaid(installment)) {
    return false;
  }
  if (installment && (installment.status === "Inadimplente" || installment.status === "Ajuizado")) {
    return true;
  }
  if (installment && installment.due_date) {
    return moment(installment.due_date, "YYYY-MM-DD").isBefore(moment(), "day");
  }
  return false;
};

/**
 * Resume as parcelas num único status. Exportado para o card decidir a borda
 * de destaque (urgente) sem duplicar a lógica.
 */
export var summarizeInstallments = function(installments, isDone) {
  if (!Array.isArray(installments) || installments.length === 0) {
    return null;
  }

  var total = installments.length;
  var paidCount = 0;
  var billed = 0;
  var open = 0;
  var overdueCount = 0;
  var overdueAmount = 0;

  installments.forEach(function(p) {
    var amount = parseFloat(p.amount);
    if (isNaN(amount)) {
      amount = 0;
    }
    billed += amount;
    if (isPaid(p)) {
      paidCount++;
    } else {
      open += amount;
      if (isOverdue(p)) {
        overdueCount++;
        overdueAmount += amount;
      }
    }
  });

  var kind, color, icon, label;
  if (open <= 0) {
    kind = "paid";
    color = "#52c41a";
    icon = "check-circle";
    label = "Quitado · " + money(billed);
  } else if (overdueCount > 0) {
    kind = "overdue";
    color = "#cf1322";
    icon = "exclamation-circle";
    label = money(overdueAmount) + " vencido · " + paidCount + "/" + total;
  } else if (isDone) {
    kind = "done_open";
    color = "#cf1322";
    icon = "exclamation-circle";
    label = money(open) + " em aberto · " + paidCount + "/" + total;
  } else {
    kind = "pending";
    color = "#fa8c16";
    icon = "clock-circle";
    label = paidCount + "/" + total + " pagas · " + money(open) + " a receber";
  }

  return {
    total: total,
    paidCount: paidCount,
    open: open,
    billed: billed,
    overdueCount: overdueCount,
    kind: kind,
    color: color,
    icon: icon,
    label: label,
    isUrgent: kind === "overdue" || kind === "done_open",
  };
};

var buildTooltip = function(installments) {
  return (
    <div style={{ minWidth: 220 }}>
      {installments.map(function(p, index) {
        var quota = p && p.quota ? p.quota : "-";
        var due = p && p.due_date ? moment(p.due_date, "YYYY-MM-DD").format("DD/MM/YYYY") : "-";
        var method = p && PAYMENT_METHODS[p.method] ? PAYMENT_METHODS[p.method] : "—";
        var status = isPaid(p) ? "Pago" : (isOverdue(p) ? "Vencida" : "Em aberto");
        var statusColor = isPaid(p) ? "#52c41a" : (isOverdue(p) ? "#ff7875" : "#d9d9d9");
        return (
          <TooltipRow key={index}>
            <span>
              <b>{quota}</b> · {money(p.amount)} · vence {due} · {method}
            </span>
            <span style={{ color: statusColor, fontWeight: 600 }}>{status}</span>
          </TooltipRow>
        );
      })}
    </div>
  );
};

export var ParcelasIndicator = function(props) {
  var installments = props.installments;
  var summary = summarizeInstallments(installments, props.isDone);

  if (!summary) {
    return null;
  }

  return (
    <Tooltip title={buildTooltip(installments)} placement="bottomLeft">
      <Line color={summary.color}>
        <Icon type={summary.icon} />
        {summary.label}
      </Line>
    </Tooltip>
  );
};

export default ParcelasIndicator;
